import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const VendorSentMessages = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    if (user) {
      fetchSentMessages();
    }
  }, [user]);

  const fetchSentMessages = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/favorites/messages/sent', { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        console.log('📤 Raw sent messages data:', data);
        
        // Group messages by subject + content to deduplicate
        const grouped = {};
        data.forEach(msg => {
          const key = (msg.subject || '') + '|||' + (msg.content || '');
          if (!grouped[key]) {
            grouped[key] = {
              ...msg,
              recipientCount: 0,
              openedCount: 0,
              repliedCount: 0
            };
          }
          // Aggregate counts
          grouped[key].recipientCount += msg.recipientCount || 0;
          grouped[key].openedCount += msg.openedCount || 0;
          grouped[key].repliedCount += msg.repliedCount || 0;
          // Keep the earliest sent time
          if (msg.sentAt && (!grouped[key].sentAt || msg.sentAt < grouped[key].sentAt)) {
            grouped[key].sentAt = msg.sentAt;
          }
          // Keep the first status (they should all be the same)
          if (!grouped[key].status || grouped[key].status === 'sent') {
            grouped[key].status = msg.status || 'sent';
          }
        });
        
        const groupedMessages = Object.values(grouped);
        console.log('📤 Grouped messages:', groupedMessages);
        setMessages(groupedMessages);
      }
    } catch (error) {
      console.error('Error fetching sent messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      sent: 'bg-gray-100 text-gray-600',
      delivered: 'bg-blue-100 text-blue-600',
      opened: 'bg-green-100 text-green-600',
      replied: 'bg-purple-100 text-purple-600',
      unsubscribed: 'bg-red-100 text-red-600'
    };
    return badges[status] || badges.sent;
  };

  const getStatusLabel = (status) => {
    const labels = {
      sent: 'Sent',
      delivered: 'Delivered',
      opened: 'Opened',
      replied: 'Replied',
      unsubscribed: 'Unsubscribed'
    };
    return labels[status] || status;
  };

  const getOpenRate = (message) => {
    if (!message.recipientCount || message.recipientCount === 0) return 0;
    return Math.round((message.openedCount || 0) / message.recipientCount * 100);
  };

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Please login to view your sent messages</p>
      </div>
    );
  }

  return (
    <div className="vendor-sent-messages">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-medium text-gray-700">📤 Messages Sent to Followers</h4>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="border rounded-lg p-4 animate-pulse bg-gray-50 h-20" />
          ))}
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <p className="text-4xl mb-2">📤</p>
          <p className="text-gray-600">You haven't sent any messages yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Send messages to your followers from the Message tab
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map((message) => (
            <div 
              key={message._id || message.subject + message.content}
              className="border rounded-lg overflow-hidden hover:shadow-md transition cursor-pointer bg-white"
              onClick={() => setSelectedMessage(message)}
            >
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm truncate">
                        {message.subject}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadge(message.status)}`}>
                        {getStatusLabel(message.status)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 flex-wrap">
                      <span className="text-xs text-gray-500">
                        {formatDate(message.sentAt)}
                      </span>
                      <span className="text-xs text-gray-500">
                        👥 {message.recipientCount || 0} followers
                      </span>
                      {message.recipientCount > 0 && (
                        <span className="text-xs text-gray-500">
                          📊 {getOpenRate(message)}% opened
                        </span>
                      )}
                      {message.repliedCount > 0 && (
                        <span className="text-xs text-gray-500">
                          💬 {message.repliedCount} replies
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-gray-400 text-sm">→</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <div>
                <h3 className="font-semibold text-lg">{selectedMessage.subject}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-gray-600">
                    Sent: {formatDate(selectedMessage.sentAt)}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadge(selectedMessage.status)}`}>
                    {getStatusLabel(selectedMessage.status)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedMessage(null)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>
            
            <div className="p-4 flex-1 overflow-y-auto">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-gray-800 whitespace-pre-wrap">
                  {selectedMessage.content}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="border rounded-lg p-3 text-center">
                  <p className="text-sm font-semibold">{selectedMessage.recipientCount || 0}</p>
                  <p className="text-xs text-gray-500">Followers</p>
                </div>
                <div className="border rounded-lg p-3 text-center">
                  <p className="text-sm font-semibold">
                    {selectedMessage.recipientCount > 0 
                      ? Math.round((selectedMessage.openedCount || 0) / selectedMessage.recipientCount * 100)
                      : 0}%
                  </p>
                  <p className="text-xs text-gray-500">Open Rate</p>
                </div>
                <div className="border rounded-lg p-3 text-center">
                  <p className="text-sm font-semibold">{selectedMessage.repliedCount || 0}</p>
                  <p className="text-xs text-gray-500">Replies</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorSentMessages;
